import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import styled from 'styled-components';
import MainLayout from '../../../layouts/MainLayout';
import { colors, breakpoints } from '../../../assets/styles/GlobalStyles';
import { getPedidosByDepartamento } from '../../requerimentos/config';
import { getContextoAcesso, isPedidoVisivelParaUsuario, podeAcessarCertidoesOnline } from '../../requerimentos/core/services/acessoPedidoService';
import { authService } from '../../../services';
import { FaFileAlt, FaUserGraduate, FaUsers, FaCertificate, FaArrowRight, FaIdCard, FaExclamationCircle, FaClipboardList, FaFileSignature } from 'react-icons/fa';

const PageContainer = styled.div`
  min-height: 100vh;
  // Recorta os círculos decorativos (::before/::after vazam 100px da borda).
  // Só overflow-x: hidden faria o eixo Y virar "auto" e a Home ganharia uma
  // barra de rolagem própria, além da barra da página (#root).
  overflow: hidden;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    top: -100px;
    right: -100px;
    width: 300px;
    height: 300px;
    background: radial-gradient(circle, ${colors.blueDark}08 0%, transparent 70%);
    border-radius: 50%;
    pointer-events: none;
    z-index: 0;
  }
  
  &::before {
    content: '';
    position: absolute;
    bottom: -100px;
    left: -100px;
    width: 300px;
    height: 300px;
    background: radial-gradient(circle, ${colors.blueDark}05 0%, transparent 70%);
    border-radius: 50%;
    pointer-events: none;
    z-index: 0;
  }
`;

const DashboardContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 120px 2rem 2rem 2rem;
  position: relative;
  z-index: 1;

  @media (max-width: ${breakpoints.mobile}) {
    padding: 100px 1.5rem 2rem 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 90px 1rem 2rem 1rem;
  }
`;

const WelcomeSection = styled.div`
  margin-bottom: 2rem;
  animation: fadeInUp 0.5s ease-out;

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const WelcomeMessage = styled.h1`
  color: ${colors.darkBlue};
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  letter-spacing: -0.5px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;

  @media (max-width: ${breakpoints.mobile}) {
    font-size: 1.75rem;
  }

  @media (max-width: 480px) {
    font-size: 1.5rem;
  }
`;

const WelcomeSubtitle = styled.p`
  color: #64748b;
  font-size: 1rem;
  margin-top: 0;
  font-weight: 400;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;

  @media (max-width: ${breakpoints.mobile}) {
    font-size: 0.9rem;
  }
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(280px, 100%), 1fr));
  gap: 1.5rem;
  margin-top: 2rem;
  animation: fadeInUp 0.5s ease-out 0.1s both;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;

  @media (max-width: ${breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const ServiceCard = styled.div`
  background: white;
  border-radius: 20px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  transition: all 0.3s ease;
  border: 1px solid rgba(0, 0, 0, 0.05);
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.$disabled ? 0.55 : 1};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, ${colors.darkBlue}, ${colors.lightBlue});
    transform: scaleX(0);
    transition: transform 0.3s ease;
  }

  ${props => !props.$disabled && `
    &:hover {
      transform: translateY(-5px);
      box-shadow: 0 12px 28px rgba(0, 0, 0, 0.1);
      border-color: rgba(124, 176, 235, 0.3);

      &::before {
        transform: scaleX(1);
      }

      .arrow-icon {
        transform: translateX(4px);
        opacity: 1;
      }
    }
  `}

  @media (max-width: ${breakpoints.mobile}) {
    padding: 1.25rem;

    &:hover {
      transform: ${props => props.$disabled ? 'none' : 'translateY(-2px)'};
    }
  }
`;

const CardTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
`;

const Badge = styled.span`
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 0.25rem 0.6rem;
  border-radius: 100px;
  white-space: nowrap;
  margin-top: 0.1rem;

  ${props => props.$variant === 'soon' ? `
    color: ${colors.textMuted};
    background: ${colors.backgroundLight};
    border: 1px solid ${colors.borderLight};
  ` : `
    color: ${colors.darkBlue};
    background: ${colors.lightBlue}22;
  `}
`;

const CardIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: linear-gradient(135deg, ${colors.blueLight}15, ${colors.blueDark}15);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  
  svg {
    font-size: 28px;
    color: ${colors.darkBlue};
  }

  @media (max-width: ${breakpoints.mobile}) {
    width: 48px;
    height: 48px;
    
    svg {
      font-size: 24px;
    }
  }
`;

const CardTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 600;
  color: ${colors.darkBlue};
  margin: 0 0 0.5rem 0;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;

  @media (max-width: ${breakpoints.mobile}) {
    font-size: 1.1rem;
  }
`;

const CardDescription = styled.p`
  font-size: 0.85rem;
  color: ${colors.textMuted};
  margin: 0;
  // Reserva o canto da seta (ArrowIcon é absoluto no canto inferior direito)
  padding-right: 1.75rem;
  line-height: 1.4;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`;

const ArrowIcon = styled.div`
  position: absolute;
  bottom: 1.5rem;
  right: 1.5rem;
  opacity: 0.6;
  transition: all 0.3s ease;
  
  svg {
    font-size: 1rem;
    color: ${colors.lightBlue};
  }

  @media (max-width: ${breakpoints.mobile}) {
    bottom: 1.25rem;
    right: 1.25rem;
  }
`;

const Home = () => {
  const { user, verifyToken } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const interval = setInterval(() => {
      verifyToken();
    }, 300000);

    return () => clearInterval(interval);
  }, [verifyToken]);

  const contextoAcesso = getContextoAcesso(user, authService.getOABData());
  const contarPedidosVisiveis = (departamentoId) => getPedidosByDepartamento(departamentoId)
    .filter(p => isPedidoVisivelParaUsuario(p, contextoAcesso)).length;
  const totalPedidosSelecaoInscricao = contarPedidosVisiveis('selecao-inscricao');
  const totalPedidosComissoes = contarPedidosVisiveis('comissoes');
  const totalPedidosAtualizacaoCadastral = contarPedidosVisiveis('atualizacao-cadastral');
  const totalPedidosCertidoes = contarPedidosVisiveis('certidoes');

  // Card de departamento sem nenhum pedido disponível pra quem está logado
  // (ex: Comissões pra PF não inscrita; Certidões pra PJ) some da Home, em vez
  // de abrir uma lista vazia.
  const serviceCards = [
    {
      id: 'protocolo',
      title: 'Protocolo Online',
      description: 'Protocolize documentos e acompanhe processos digitalmente',
      icon: FaFileAlt,
      action: () => navigate('/protocolo-online')
    },
    {
      id: 'consulta-protocolos',
      title: 'Consulta de Protocolos',
      description: 'Acompanhe o andamento dos seus protocolos',
      icon: FaClipboardList,
      action: () => navigate('/consulta-protocolos')
    },
    {
      id: 'selecao',
      title: 'Seleção e Inscrição',
      description: 'Pedidos de Advogados, Pedidos de Estagiário, Pedidos de Sociedade e Pedidos Gerais',
      icon: FaUserGraduate,
      badge: `${totalPedidosSelecaoInscricao} pedidos`,
      hidden: totalPedidosSelecaoInscricao === 0,
      action: () => navigate('/requerimentos/selecao-inscricao/pedidos')
    },
    {
      id: 'comissoes',
      title: 'Comissões',
      description: 'Solicite inscrição e participe das comissões da OAB',
      icon: FaUsers,
      badge: `${totalPedidosComissoes} pedidos`,
      hidden: totalPedidosComissoes === 0,
      action: () => navigate('/requerimentos/comissoes/pedidos')
    },
    {
      id: 'atualizacao-cadastral',
      title: 'Atualização Cadastral',
      description: 'Atualize dados cadastrais e de subseção junto à OAB/MS',
      icon: FaIdCard,
      badge: `${totalPedidosAtualizacaoCadastral} pedidos`,
      hidden: totalPedidosAtualizacaoCadastral === 0,
      action: () => navigate('/requerimentos/atualizacao-cadastral/pedidos')
    },
    {
      id: 'certidoes',
      title: 'Certidões',
      description: 'Solicite e acompanhe emissão de certidões',
      icon: FaCertificate,
      badge: `${totalPedidosCertidoes} pedidos`,
      hidden: totalPedidosCertidoes === 0,
      action: () => navigate('/requerimentos/certidoes/pedidos')
    },
    {
      id: 'certidoes-online',
      title: 'Certidões Online',
      description: 'Emita certidões automáticas a qualquer hora, como a Certidão Nada Consta',
      icon: FaFileSignature,
      hidden: !podeAcessarCertidoesOnline(contextoAcesso),
      action: () => navigate('/certidoes-online')
    },
    {
      id: 'pendencias',
      title: 'Pendências',
      description: 'Resolva pendências de pedidos feitos dentro do portal',
      icon: FaExclamationCircle,
      action: () => navigate('/pendencias')
    }
  ].filter(card => !card.hidden);

  const handleCardClick = (card) => {
    if (!card.disabled && card.action) {
      card.action();
    }
  };

  if (!user) {
    return (
      <MainLayout>
        <PageContainer>
          <DashboardContainer>
            <div style={{ textAlign: 'center', padding: '3rem' }}>
              <p>Carregando...</p>
            </div>
          </DashboardContainer>
        </PageContainer>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <PageContainer>
        <DashboardContainer>
          <WelcomeSection>
            <WelcomeMessage>
              Olá, {user.nome?.split(' ')[0] || 'Usuário'}! 👋
            </WelcomeMessage>
            <WelcomeSubtitle>
              Bem-vindo à Central de Solicitações OAB/MS
            </WelcomeSubtitle>
          </WelcomeSection>

          <CardsGrid>
            {serviceCards.map((card) => {
              const Icon = card.icon;
              return (
                <ServiceCard
                  key={card.id}
                  $disabled={card.disabled}
                  onClick={() => handleCardClick(card)}
                >
                  <CardTop>
                    <CardIcon>
                      <Icon />
                    </CardIcon>
                    {card.disabled && <Badge $variant="soon">Em breve</Badge>}
                    {card.badge && !card.disabled && <Badge>{card.badge}</Badge>}
                  </CardTop>
                  <CardTitle>{card.title}</CardTitle>
                  <CardDescription>{card.description}</CardDescription>
                  {!card.disabled && (
                    <ArrowIcon className="arrow-icon">
                      <FaArrowRight />
                    </ArrowIcon>
                  )}
                </ServiceCard>
              );
            })}
          </CardsGrid>
        </DashboardContainer>
      </PageContainer>
    </MainLayout>
  );
};

export default Home;